import { useEffect, useContext } from 'react';
import { useLocation } from 'react-router';

import { FeedbackUIContext } from '#context/feedback-ui-context';
import { useGetUserMutation } from '#services/user';

export interface UserFormState {
  username: string;
  password: string;
  forceAdminPasswordReset: boolean;

  name: string;
  code: string;
  email: string;
  phone: string;
  address: string;

  disableShippingInfo: boolean;
  shippingName: string;
  shippingEmail: string;
  shippingPhone: string;
  shippingAddress: string;

  disableBillingInfo: boolean;
  billingName: string;
  billingEmail: string;
  billingPhone: string;
  billingAddress: string;

  availableCollections: unknown[];
  collectionRestrictionIds: unknown[];

  salespersonCustomerIds: unknown[];
  sampleItemsPercent: string;
  reorderItemsPercent: string;
  relatedSalespersonId: string;
  availableCustomers: unknown[];
  availableSalesperson: unknown[];

  role: string;
  region: string;

  createdBy: string;
  [key: string]: unknown;
}

export const getInitialState = (plainData?: unknown): UserFormState => {
  const initialState: UserFormState = {
    username: '',
    password: '',
    forceAdminPasswordReset: false,

    name: '',
    code: '',
    email: '',
    phone: '',
    address: '',

    disableShippingInfo: false,
    shippingName: '',
    shippingEmail: '',
    shippingPhone: '',
    shippingAddress: '',

    disableBillingInfo: false,
    billingName: '',
    billingEmail: '',
    billingPhone: '',
    billingAddress: '',

    availableCollections: [],
    collectionRestrictionIds: [],

    salespersonCustomerIds: [],
    sampleItemsPercent: '',
    reorderItemsPercent: '',
    relatedSalespersonId: '',
    availableCustomers: [],
    availableSalesperson: [],

    role: 'customer',
    region: 'US', // default for NEW users only — see below

    createdBy: '',
  };

  // workaround for redux toolkit
  // TODO use redux to handle the state
  let state = plainData
    ? JSON.parse(JSON.stringify(plainData))
    : initialState;
  state = Object.assign({}, initialState, state);

  // Don't apply the 'US' default to existing users who have no region stored
  // in the DB. JSON.stringify drops undefined, so the key is absent from the
  // parsed state and Object.assign would fill it from initialState, silently
  // writing region='US' on the next save and flipping their mail locale to EN.
  // For existing users, keep an empty string until the admin makes an
  // explicit choice.
  if (plainData && !(plainData as { region?: string }).region) {
    state.region = '';
  }

  return state;
};

export function useData({
  resetForm,
}: {
  resetForm: (state: UserFormState) => void;
}) {
  const location = useLocation();

  const {
    showBackdrop,
    hideBackdrop,
    simpleSnackbar,
  } = useContext(FeedbackUIContext)!;

  const [getUser] = useGetUserMutation();

  useEffect(() => {
    const userId = location.search.match(/userId=(\S+)/i)?.[1] || '';

    showBackdrop();
    void getUser({ userId })
      .then((res) => {
        hideBackdrop();

        const hasError = simpleSnackbar(res as unknown as Parameters<typeof simpleSnackbar>[0]);
        if (hasError) return;

        const data = res.data as {
          id?: string | number;
          availableCustomers: Array<{ id: string | number; relatedSalespersonId?: string | number }>;
        };
        const salespersonCustomerIds = data.availableCustomers.filter(
          (ac) => (data.id && ac.relatedSalespersonId === data.id),
        ).map((ac) => ac.id);
        const newData = { ...data, salespersonCustomerIds };

        if (userId) {
          resetForm(getInitialState(newData));
        } else {
          resetForm(newData as unknown as UserFormState);
        }
      });
  }, []);
}
